import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import {
  MessageCircle,
  BarChart3,
  Ear,
  SearchCheck,
  ShieldAlert,
  Mail,
  Swords,
  Handshake,
  RefreshCw,
  FileText,
  type LucideIcon,
} from 'lucide-react';

interface CatalogEntry {
  agentId: string;
  name: string;
  description: string;
  triggerDescription: string;
  enabled: boolean;
  confidenceThreshold: number;
}

interface ActionEntry {
  id: string;
  agentId: string;
  status: string;
  confidence: number | null;
  reasoning: string | null;
  errorMessage: string | null;
  createdAt: string | null;
  repAction: string | null;
}

const AGENT_ICONS: Record<string, LucideIcon> = {
  A1_WHATSAPP_PROSPECTING: MessageCircle,
  A2_LEAD_SCORING: BarChart3,
  A3_SOCIAL_LISTENING: Ear,
  A4_GAP_DETECTION: SearchCheck,
  A5_RISK_SCORING: ShieldAlert,
  A6_FOLLOW_UP_DRAFTING: Mail,
  A7_COMPETITIVE_INTELLIGENCE: Swords,
  A8_ONBOARDING_BOT: Handshake,
  A9_RENEWAL_SIGNAL: RefreshCw,
  A10_QBR_PREPARATION: FileText,
};

const STATUS_COLORS: Record<string, string> = {
  SUCCESS: 'bg-emerald-900/40 text-emerald-200',
  FAILED: 'bg-red-900/40 text-red-200',
  SKIPPED_LOW_CONFIDENCE: 'bg-amber-900/40 text-amber-200',
  RETRIED_AND_RECOVERED: 'bg-blue-900/40 text-blue-200',
};

export function AgentsPage() {
  const { t } = useTranslation();
  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  const [logs, setLogs] = useState<ActionEntry[]>([]);
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [catRes, logRes] = await Promise.all([
        apiClient.get<CatalogEntry[]>('/agents/catalog'),
        apiClient.get<ActionEntry[]>('/agents/log'),
      ]);
      setCatalog(catRes.data);
      setLogs(logRes.data);
    } catch {
      setCatalog([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  async function toggleAgent(agentId: string, enable: boolean) {
    const path = enable ? 'enable' : 'disable';
    await apiClient.post(`/agents/settings/${agentId}/${path}`, {});
    showToast(`${agentId} ${enable ? 'enabled' : 'disabled'}`);
    load();
  }

  async function setThreshold(agentId: string, threshold: number) {
    await apiClient.post(`/agents/settings/${agentId}/threshold`, {
      threshold,
    });
    setCatalog((prev) =>
      prev.map((a) =>
        a.agentId === agentId ? { ...a, confidenceThreshold: threshold } : a,
      ),
    );
  }

  async function markAction(
    id: string,
    action: 'ACCEPTED' | 'EDITED' | 'REJECTED',
  ) {
    await apiClient.post(`/agents/log/${id}/rep-action`, { action });
    load();
  }

  return (
    <div className="space-y-6">
      {toast && (
        <div className="fixed right-6 top-20 z-50 rounded-lg border bg-card px-4 py-2 shadow-lg text-sm">
          {toast}
        </div>
      )}

      <div>
        <h1 className="text-2xl font-semibold">{t('nav.agents')}</h1>
        <p className="text-sm text-muted-foreground">
          AI agents that analyze your pipeline, score leads, draft
          follow-ups, and flag risks automatically. Enable the ones
          that fit your workflow and adjust how confident they need to
          be before surfacing results.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {catalog.map((agent) => {
          const Icon = AGENT_ICONS[agent.agentId] ?? FileText;
          const agentLogs = logs
            .filter((l) => l.agentId === agent.agentId)
            .slice(0, 5);
          const isExpanded = expandedAgent === agent.agentId;

          return (
            <div
              key={agent.agentId}
              className={`rounded-xl border bg-card p-5 transition-shadow ${
                agent.enabled ? 'shadow-sm' : 'opacity-70'
              }`}
            >
              {/* Header */}
              <div className="flex items-start gap-3">
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                    agent.enabled
                      ? 'bg-primary/10 text-primary'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">{agent.name}</h3>
                    <button
                      onClick={() =>
                        toggleAgent(agent.agentId, !agent.enabled)
                      }
                      className={`relative h-6 w-11 rounded-full transition-colors ${
                        agent.enabled ? 'bg-primary' : 'bg-muted'
                      }`}
                      title={agent.enabled ? 'Disable' : 'Enable'}
                    >
                      <span
                        className={`absolute top-0.5 h-5 w-5 rounded-full bg-card shadow transition-transform ${
                          agent.enabled
                            ? 'translate-x-5'
                            : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {agent.description}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Trigger: {agent.triggerDescription}
                  </p>
                </div>
              </div>

              {/* Threshold slider */}
              <div className="mt-4 flex items-center gap-3">
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  Confidence threshold
                </span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={agent.confidenceThreshold}
                  onChange={(e) =>
                    setThreshold(agent.agentId, parseInt(e.target.value))
                  }
                  className="flex-1 accent-primary"
                />
                <span className="w-8 text-right text-xs font-medium">
                  {agent.confidenceThreshold}%
                </span>
              </div>

              {/* Recent runs toggle */}
              <div className="mt-3">
                <button
                  onClick={() =>
                    setExpandedAgent(isExpanded ? null : agent.agentId)
                  }
                  className="text-xs text-primary hover:underline"
                >
                  {isExpanded
                    ? 'Hide recent runs'
                    : `Recent runs (${agentLogs.length})`}
                </button>

                {isExpanded && (
                  <ul className="mt-2 space-y-2">
                    {agentLogs.map((entry) => (
                      <li
                        key={entry.id}
                        className="rounded-lg border bg-muted/50 p-3 text-sm"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span
                              className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                STATUS_COLORS[entry.status] ??
                                'bg-muted text-foreground'
                              }`}
                            >
                              {entry.status}
                            </span>
                            {entry.confidence !== null && (
                              <span className="text-xs text-muted-foreground">
                                {entry.confidence}% confidence
                              </span>
                            )}
                          </div>
                          {entry.createdAt && (
                            <span className="text-xs text-muted-foreground">
                              {new Date(entry.createdAt).toLocaleDateString(
                                'en-US',
                                {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                },
                              )}
                            </span>
                          )}
                        </div>
                        {entry.reasoning && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {entry.reasoning}
                          </p>
                        )}
                        {entry.errorMessage && (
                          <p className="mt-1 text-xs text-red-600">
                            {entry.errorMessage}
                          </p>
                        )}
                        {entry.status === 'SUCCESS' &&
                          entry.repAction === null && (
                            <div className="mt-2 flex gap-1">
                              <Button
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() =>
                                  markAction(entry.id, 'ACCEPTED')
                                }
                              >
                                Accept
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                onClick={() =>
                                  markAction(entry.id, 'EDITED')
                                }
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                className="h-7 text-xs"
                                onClick={() =>
                                  markAction(entry.id, 'REJECTED')
                                }
                              >
                                Reject
                              </Button>
                            </div>
                          )}
                        {entry.repAction && (
                          <span className="mt-1 inline-block text-xs text-muted-foreground">
                            Rep: {entry.repAction}
                          </span>
                        )}
                      </li>
                    ))}
                    {agentLogs.length === 0 && (
                      <li className="text-xs text-muted-foreground">
                        No runs yet
                      </li>
                    )}
                  </ul>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
